'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

const HowItWorksSection = () => {
  const { t } = useLanguage();
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const steps = [
    { icon: 'ShareIcon', stepKey: 'step1', color: '#1D6FE8' },
    { icon: 'CalendarDaysIcon', stepKey: 'step2', color: '#0A1628' },
    { icon: 'UserPlusIcon', stepKey: 'step3', color: '#C9A84C' },
    { icon: 'BanknotesIcon', stepKey: 'step4', color: '#16A34A' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [isVisible, steps.length]);

  return (
    <section ref={sectionRef} id="como-funciona" className="py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-[#1D6FE8]" />
            <span className="text-sm font-body font-medium text-[#1D6FE8] tracking-wide uppercase">
              {t('howItWorks.eyebrow')}
            </span>
          </div>
          <h2 className="font-display font-bold text-[#0A1628] leading-tight tracking-tight mb-6"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
            {t('howItWorks.headline')}{' '}
            <span className="gradient-text-blue">{t('howItWorks.headlineAccent')}</span>
          </h2>
          <p className="font-body text-lg text-[#4A5568] leading-relaxed">
            {t('howItWorks.subtitle')}
          </p>
        </div>

        {/* Horizontal Timeline */}
        <div className="relative">
          {/* Progress line */}
          <div className="hidden md:block absolute top-12 left-0 right-0 h-px bg-[#E8EDF5] mx-16">
            <div
              className="h-full bg-gradient-to-r from-[#1D6FE8] to-[#C9A84C] transition-all duration-700 ease-out-expo"
              style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-4">
            {steps.map((step, index) => (
              <div
                key={step.stepKey}
                className={`relative flex flex-col items-center text-center cursor-pointer transition-all duration-450 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
                onClick={() => setActiveStep(index)}
              >
                {/* Step number + icon */}
                <div className={`relative w-24 h-24 rounded-2xl flex items-center justify-center mb-6 transition-all duration-450 ${
                  activeStep === index
                    ? 'bg-[#0A1628] shadow-cta scale-110'
                    : 'bg-[#F4F7FC] hover:bg-[#EEF4FD]'
                }`}>
                  <Icon
                    name={step.icon as any}
                    size={32}
                    className={`transition-colors duration-350 ${
                      activeStep === index ? 'text-white' : 'text-[#4A5568]'
                    }`}
                  />
                  <div className={`absolute -top-3 -right-3 w-7 h-7 rounded-full flex items-center justify-center text-xs font-display font-bold transition-all duration-350 ${
                    activeStep === index
                      ? 'bg-[#1D6FE8] text-white'
                      : 'bg-white text-[#4A5568] border border-[#E8EDF5]'
                  }`}>
                    {index + 1}
                  </div>
                </div>

                {/* Content */}
                <div className={`transition-all duration-350 ${activeStep === index ? 'opacity-100' : 'opacity-60'}`}>
                  <h3 className="font-display font-semibold text-[#0A1628] text-lg mb-2">
                    {t(`howItWorks.${step.stepKey}`)}
                  </h3>
                  <p className="font-body text-sm text-[#4A5568] leading-relaxed max-w-48 mx-auto">
                    {t(`howItWorks.${step.stepKey}Desc`)}
                  </p>
                </div>

                {/* Mobile connector */}
                {index < steps.length - 1 && (
                  <div className="md:hidden mt-6 flex flex-col items-center">
                    <div className="w-px h-8 bg-[#E8EDF5]" />
                    <Icon name="ChevronDownIcon" size={16} className="text-[#E8EDF5]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
